












































import { Component, Vue, Watch } from 'vue-property-decorator'
import { ReferralStatus } from '@/api/types'
import { ReportAPI } from '@/api/client/report'
import { ChartData, ChartOptions } from 'chart.js'
import ChartDataLabels from 'chartjs-plugin-datalabels'

interface ConversionData {
    currentYear: ConversionYearData,
    previousYear: ConversionYearData,
}

interface ConversionYearData {
    successful: number,
    closed: number,
}

@Component
export default class ConversionBlock extends Vue {
    loading: boolean = false
    data: ConversionData = null
    view: string = 'user'

    mounted() {
        this.view = this.$currentUser.defaultDashboardView
        this.loadData()
    }

    get availableViews(): string[] {
        return this.$currentUser.isTeamLead ? ['user', 'branches'] : ['user']
    }

    get availableViewsOptions() {
        return this.availableViews
            .map(availableView => ({
                label: this.$t(`dashboard.conversion.view.${availableView}`),
                value: availableView,
            }))
    }

    changeView(view: string): void {
        if (!this.availableViews.includes(view)) {
            return
        }

        this.view = view
    }

    @Watch('view')
    reloadData() {
        this.loadData()
    }

    async loadData(): Promise<void> {
        this.loading = true
        this.data = await ReportAPI.getReferralsConversion(this.view)
        this.loading = false
    }

    get conversion(): number {
        if (!this.data) {
            return 0
        }

        return this.calculateConversion(this.data.currentYear)
    }

    get previousConversion(): number {
        if (!this.data) {
            return 0
        }

        return this.calculateConversion(this.data.previousYear)
    }

    calculateConversion(data: ConversionYearData) {
        const successfulReferralsCount = data.successful
        const closedReferralsCount = data.closed
        const totalReferralsCount = successfulReferralsCount + closedReferralsCount

        if (totalReferralsCount === 0) {
            return 0
        }

        return successfulReferralsCount / totalReferralsCount
    }

    get delta(): number {
        return this.conversion - this.previousConversion
    }

    get deltaLabel(): string {
        const delta = Math.round(this.delta * 100)

        if (delta > 0) {
            return 'up'
        } else if (delta < 0) {
            return 'down'
        } else {
            return 'equal'
        }
    }

    get chartData(): ChartData {
        if (!this.data) {
            return null
        }

        const labels: string[] = []
        const data: number[] = []
        const backgroundColor: string[] = []
        const datalabels = {
            color: 'rgba(255, 255, 255, .75)',
        }

        if (this.data.currentYear.successful > 0) {
            labels.push(this.$t(`referral.status.${ReferralStatus.SUCCESSFUL}`).toString())
            data.push(this.data.currentYear.successful)
            backgroundColor.push('rgb(0, 163, 137)')
        }

        if (this.data.currentYear.closed > 0) {
            labels.push(this.$t(`referral.status.${ReferralStatus.CLOSED}`).toString())
            data.push(this.data.currentYear.closed)
            backgroundColor.push('rgb(222, 64, 119)')
        }

        return {
            labels,
            datasets: [
                {
                    data,
                    backgroundColor,
                    datalabels,
                },
            ],
        }
    }

    get chartOptions(): ChartOptions<'doughnut'> {
        if (!this.data) {
            return null
        }

        const successfulReferralsCount = this.data.currentYear.successful
        const closedReferralsCount = this.data.currentYear.closed
        const totalReferralsCount = successfulReferralsCount + closedReferralsCount

        let rotation = 0
        if (totalReferralsCount > 0) {
            rotation -= (successfulReferralsCount / totalReferralsCount) * 360
        }

        return {
            plugins: {
                legend: {
                    display: false,
                },
                tooltip: {
                    enabled: false,
                },
            },
            rotation,
        }
    }

    get chartPlugins(): object[] {
        return [
            ChartDataLabels,
        ]
    }
}
